import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Input, Card, CardBody } from '@heroui/react';
import { Mail, Shield, ArrowLeft, RefreshCw } from 'lucide-react';
import { useAuth } from '@src/presentation/contexts/AuthContext';
import { ToastService } from '@src/shared/services/toast.service';
import { getOnboardingRoute } from '@src/application/users/dto/auth-response.dto';

type AuthStep = 'email' | 'otp';

export default function AdminLoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { sendOtp, verifyOtp, loading: authLoading, isAuthenticated, profileState, user } = useAuth();

    const [step, setStep] = useState<AuthStep>('email');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [errors, setErrors] = useState<{ email?: string; otp?: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [otpExpiresIn, setOtpExpiresIn] = useState(0);

    const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Handle cooldown timer
    useEffect(() => {
        if (cooldown > 0) {
            const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [cooldown]);

    // Handle OTP expiration timer
    useEffect(() => {
        if (otpExpiresIn > 0) {
            const timer = setTimeout(() => setOtpExpiresIn(otpExpiresIn - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [otpExpiresIn]);

    // Redirect after successful authentication
    useEffect(() => {
        if (isAuthenticated && profileState && user) {
            // Check if user has admin permissions
            const hasAdminPermissions = user.permissions && user.permissions.length > 0;

            if (!hasAdminPermissions) {
                ToastService.error('You do not have permission to access the admin panel');
                navigate('/', { replace: true });
                return;
            }

            if (profileState === 'ACTIVE') {
                const from = (location.state as any)?.from?.pathname || '/dashboard';
                navigate(from, { replace: true });
            } else {
                // Redirect to appropriate onboarding step
                const onboardingRoute = getOnboardingRoute(profileState);
                navigate(onboardingRoute, { replace: true });
            }
        }
    }, [isAuthenticated, profileState, user, navigate, location]);

    const validateEmail = (): boolean => {
        const newErrors: { email?: string } = {};

        if (!email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateOtp = (otpArray?: string[]): boolean => {
        const otpValue = (otpArray || otp).join('');
        const newErrors: { otp?: string } = {};

        if (otpValue.length !== 6) {
            newErrors.otp = 'Please enter the complete 6-digit code';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSendOtp = async (e?: React.FormEvent) => {
        e?.preventDefault();

        if (isSubmitting || authLoading || cooldown > 0) return;

        if (!validateEmail()) {
            ToastService.error('Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);

        try {
            const result = await sendOtp(email);

            ToastService.success(result.message);
            setCooldown(result.cooldownSeconds);
            setOtpExpiresIn(result.expiresInMinutes * 60);
            setStep('otp');

            // Focus first OTP input
            setTimeout(() => {
                otpInputRefs.current[0]?.focus();
            }, 100);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to send OTP';
            ToastService.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleVerifyOtp = async (e?: React.FormEvent, otpArray?: string[]) => {
        e?.preventDefault();

        if (isSubmitting || authLoading) return;

        const otpToValidate = otpArray || otp;

        if (!validateOtp(otpToValidate)) {
            ToastService.error('Please enter the complete verification code');
            return;
        }

        setIsSubmitting(true);

        try {
            const otpValue = otpToValidate.join('');
            await verifyOtp(email, otpValue);

            // Navigation happens via useEffect when profileState changes
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Invalid or expired OTP';
            ToastService.error(errorMessage);
            // Clear OTP on error
            setOtp(['', '', '', '', '', '']);
            otpInputRefs.current[0]?.focus();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOtpChange = (index: number, value: string) => {
        // Only allow numbers
        if (value && !/^\d+$/.test(value)) return;

        const newOtp = [...otp];

        // Handle paste
        if (value.length > 1) {
            const pastedValues = value.slice(0, 6).split('');
            pastedValues.forEach((char, i) => {
                if (index + i < 6) {
                    newOtp[index + i] = char;
                }
            });
            setOtp(newOtp);

            const lastIndex = Math.min(index + pastedValues.length, 5);
            otpInputRefs.current[lastIndex]?.focus();
            return;
        }

        newOtp[index] = value;
        setOtp(newOtp);

        // Clear error when user types
        if (errors.otp) {
            setErrors({});
        }

        // Auto-focus next input
        if (value && index < 5) {
            otpInputRefs.current[index + 1]?.focus();
        }

        // Auto-submit when all digits are entered
        if (value && index === 5 && newOtp.every(digit => digit)) {
            setTimeout(() => handleVerifyOtp(undefined, newOtp), 100);
        }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        }
    };

    const handleBackToEmail = () => {
        setStep('email');
        setOtp(['', '', '', '', '', '']);
        setErrors({});
    };

    const isPending = isSubmitting || authLoading;

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary px-4 py-8">
            <div className="w-full max-w-md">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded bg-primary/10 mb-4">
                        <Shield className="w-8 h-8 text-primary" />
                    </div>
                    <h1 className="text-3xl font-bold text-foreground mb-2">
                        {step === 'email' ? 'Admin Access' : 'Enter Verification Code'}
                    </h1>
                    <p className="text-foreground/60">
                        {step === 'email'
                            ? 'Sign in with your admin email'
                            : `We sent a code to ${email}`
                        }
                    </p>
                </div>

                {/* Login Form */}
                <Card className="border border-border rounded shadow-none hover:shadow-custom transition-shadow bg-card backdrop-blur-sm">
                    <CardBody className="p-6 sm:p-8">
                        {step === 'email' ? (
                            <form onSubmit={handleSendOtp} className="space-y-5">
                                {/* Email Input */}
                                <Input
                                    label="Admin Email"
                                    type="email"
                                    placeholder="admin@example.com"
                                    value={email}
                                    onValueChange={(value) => {
                                        setEmail(value);
                                        if (errors.email) setErrors({});
                                    }}
                                    isInvalid={!!errors.email}
                                    errorMessage={errors.email}
                                    startContent={<Mail className="w-4 h-4 text-default-400" />}
                                    isDisabled={isPending}
                                    isRequired
                                    classNames={{
                                        input: "font-medium",
                                        inputWrapper: "rounded border border-border",
                                    }}
                                />

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    color="primary"
                                    size="lg"
                                    className="w-full font-semibold rounded"
                                    isLoading={isPending}
                                    isDisabled={isPending}
                                >
                                    {isPending ? 'Sending code...' : 'Continue'}
                                </Button>
                            </form>
                        ) : (
                            <form onSubmit={handleVerifyOtp} className="space-y-5">
                                {/* Back button */}
                                <button
                                    type="button"
                                    onClick={handleBackToEmail}
                                    className="flex items-center gap-1 text-sm text-default-500 hover:text-primary transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Change email
                                </button>

                                {/* OTP Input */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-default-600">
                                        Verification Code
                                    </label>
                                    <div className="flex gap-2 justify-center">
                                        {otp.map((digit, index) => (
                                            <input
                                                key={index}
                                                ref={(el) => { otpInputRefs.current[index] = el; }}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={6}
                                                value={digit}
                                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                disabled={isPending}
                                                className={`
                                                    w-12 h-14 text-center text-xl font-semibold
                                                    rounded border-1.5 bg-default-50/50
                                                    transition-all duration-200
                                                    focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20
                                                    disabled:opacity-50 disabled:cursor-not-allowed
                                                    ${errors.otp ? 'border-danger' : 'border-default-200 hover:border-default-300'}
                                                `}
                                            />
                                        ))}
                                    </div>
                                    {errors.otp && (
                                        <p className="text-xs text-danger text-center">{errors.otp}</p>
                                    )}
                                </div>

                                {/* Resend Code */}
                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={() => handleSendOtp()}
                                        disabled={cooldown > 0 || isPending}
                                        className={`
                                            flex items-center gap-1 text-sm mx-auto
                                            ${cooldown > 0 || isPending
                                                ? 'text-default-300 cursor-not-allowed'
                                                : 'text-primary hover:text-primary/80 cursor-pointer'
                                            }
                                            transition-colors
                                        `}
                                    >
                                        <RefreshCw className="w-3 h-3" />
                                        {cooldown > 0
                                            ? `Resend in ${cooldown}s`
                                            : 'Resend code'
                                        }
                                    </button>
                                </div>

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    color="primary"
                                    size="lg"
                                    className="w-full font-semibold rounded"
                                    isLoading={isPending}
                                    isDisabled={isPending || otp.some(d => !d)}
                                >
                                    {isPending ? 'Verifying...' : 'Verify & Sign In'}
                                </Button>
                            </form>
                        )}

                        {/* Divider */}
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-border" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-4 bg-content1 text-foreground/60">
                                    Not an admin?
                                </span>
                            </div>
                        </div>

                        {/* Public Site Link */}
                        <div className="text-center">
                            <a
                                href="https://pascohub.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1"
                            >
                                Go to public site
                            </a>
                        </div>
                    </CardBody>
                </Card>

                {/* Security Notice */}
                <p className="text-center text-xs text-foreground/50 mt-6">
                    This is a secure admin area. All activity is monitored and logged.
                </p>
            </div>
        </div>
    );
}
